import React, { useState } from 'react';
import MultiVariantNetwork from '../MultiVariantNetwork';
import DataTable from '../DataTable';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ScatterChart, Scatter, ResponsiveContainer } from 'recharts';
import { getApiEndpoint } from '../../../config/newgenome-config';

// ---------- utils ----------
const findRx = (files, rx) => files.find(f => rx.test(f?.name || ''));
const findAllRx = (files, rx) => files.filter(f => rx.test(f?.name || ''));

const base64DataURI = (data, mime = 'application/octet-stream') => {
  // Check if data is already base64 encoded
  const isRawData = data && (data.startsWith('%PDF') || data.startsWith('\x89PNG'));

  if (isRawData) {
    // Convert raw binary data to base64
    const bytes = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      bytes[i] = data.charCodeAt(i);
    }
    const b64 = btoa(String.fromCharCode(...bytes));
    return `data:${mime};base64,${b64}`;
  } else {
    // Already base64, just clean whitespace
    const cleanB64 = (data || '').replace(/\s+/g, '');
    return `data:${mime};base64,${cleanB64}`;
  }
};

const getMime = (name = '') => {
  const n = name.toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.pdf')) return 'application/pdf';
  return 'application/octet-stream';
};

const splitGoEnrich = (txt) => {
  if (!txt) return {};
  const lines = txt.trim().split('\n');
  if (lines.length <= 1) return {};

  const headers = lines[0].split('\t');
  const lower = headers.map(h => (h || '').toLowerCase());

  // accept either "go_type" or "type"
  let typeIdx = lower.indexOf('go_type');
  if (typeIdx < 0) typeIdx = lower.indexOf('type');
  if (typeIdx < 0) return { 'GO BP':'', 'GO MF':'', 'GO CC':'' };

  const buckets = { BP: [], MF: [], CC: [] };
  for (const row of lines.slice(1)) {
    const cols = row.split('\t');
    const t = (cols[typeIdx] || '').trim().toUpperCase();
    if (t === 'BP' || t === 'BIOLOGICAL PROCESS') buckets.BP.push(row);
    if (t === 'MF' || t === 'MOLECULAR FUNCTION') buckets.MF.push(row);
    if (t === 'CC' || t === 'CELLULAR COMPONENT') buckets.CC.push(row);
  }

  const headerLine = lines[0];
  return {
    'GO BP': buckets.BP.length ? [headerLine, ...buckets.BP].join('\n') : '',
    'GO MF': buckets.MF.length ? [headerLine, ...buckets.MF].join('\n') : '',
    'GO CC': buckets.CC.length ? [headerLine, ...buckets.CC].join('\n') : ''
  };
};

const parseTSV = (txt) => {
  if (!txt) return [];
  const lines = txt.trim().split('\n');
  if (lines.length <= 1) return [];

  const headers = lines[0].split('\t');
  return lines.slice(1).map(line => {
    const values = line.split('\t');
    const obj = {};
    headers.forEach((header, i) => {
      obj[header] = values[i];
    });
    return obj;
  });
};


// ---------- special renderers ----------
const COLORS = {
  pathogenicity: {
    'Pathogenic': '#ef4444',
    'Likely_pathogenic': '#fb923c',
    'Uncertain_significance': '#a3a3a3',
    'Conflicting_classifications_of_pathogenicity': '#fde047'
  },
  variantClass: {
    'SNV': '#fde047',
    'DEL': '#6ee7b7',
    'INS': '#f87171',
    'Other': '#a3a3a3'
  },
  impact: {
    'High': '#ef4444',
    'Moderate': '#fbbf24',
    'Low': '#60a5fa',
    'Modifier': '#a3a3a3'
  }
};

const VariantSummaryRenderer = ({ section }) => {
  // Parse data files
  const clinvarData = parseTSV(section.clinvarPathogenicity || '').map(d => ({
    name: d.ClinVar_CLNSIG || d.name,
    value: parseInt(d.count) || 0
  }));

  const diseaseData = parseTSV(section.diseaseAccumulate || '')
    .slice(0, 10)
    .map((d, i) => ({
      name: d.ClinVar_CLNDN || d.name,
      count: parseInt(d.count) || 0,
      y: i
    }))
    .reverse();

  const geneData = parseTSV(section.geneAccumulate || '')
    .slice(0, 10)
    .map((d, i) => ({
      name: d.SYMBOL || d.name,
      count: parseInt(d.count) || 0,
      y: i
    }))
    .reverse();

  const variantClassData = parseTSV(section.variantClass || '').map(d => ({
    name: d.VARIANT_CLASS || d.name,
    value: parseInt(d.count) || 0
  }));

  const consequenceData = parseTSV(section.variantConsequence || '')
    .slice(0, 14)
    .map(d => ({
      name: (d.Consequence || '').replace(/_/g, ' '),
      count: parseInt(d.count) || 0,
      impact: d.Impact
    }));

  // Show images
  const images = (section.images || []).map((img, i) => (
    <div key={`img-${i}`} className="variant-summary-item">
      <h3 className="variant-summary-title">{img.name.split('/').pop().replace(/\.[^/.]+$/, '')}</h3>
      <div className="variant-summary-image-wrapper">
        <img src={base64DataURI(img.data, getMime(img.name))} alt={img.name} />
      </div>
    </div>
  ));

  return (
    <div className="variant-summary-panel">
      <div className="variant-summary-grid">
        {images}

        {clinvarData.length > 0 && (
          <div className="variant-summary-item">
            <h3 className="variant-summary-title">ClinVar Pathogenicity</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={clinvarData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {clinvarData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={COLORS.pathogenicity[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {diseaseData.length > 0 && (
          <div className="variant-summary-item" style={{ width: '100%', maxWidth: '100%' }}>
            <h3 className="variant-summary-title">Disease Accumulate (Top 10)</h3>
            <div style={{ width: '100%', height: '450px', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ left: 0, right: 0, top: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                  <XAxis
                    type="number"
                    dataKey="count"
                    label={{ value: 'Count', position: 'insideBottom', offset: -10 }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={190}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.85)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'white'
                    }}
                    itemStyle={{ color: 'white' }}
                    labelStyle={{ color: 'white' }}
                    formatter={(value) => [`Count: ${value}`]}
                  />
                  <Scatter data={diseaseData} fill="#3b82f6" shape="circle" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {geneData.length > 0 && (
          <div className="variant-summary-item" style={{ width: '100%', maxWidth: '100%' }}>
            <h3 className="variant-summary-title">Gene Accumulate (Top 10)</h3>
            <div style={{ width: '100%', height: '350px', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ left: 0, right: 0, top: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                  <XAxis
                    type="number"
                    dataKey="count"
                    label={{ value: 'Count', position: 'insideBottom', offset: -10 }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 12 }}
                    interval={0}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.85)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'white'
                    }}
                    itemStyle={{ color: 'white' }}
                    labelStyle={{ color: 'white' }}
                    formatter={(value) => [`Count: ${value}`]}
                  />
                  <Scatter data={geneData} fill="#10b981" shape="circle" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {variantClassData.length > 0 && (
          <div className="variant-summary-item">
            <h3 className="variant-summary-title">Variant Class</h3>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={variantClassData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {variantClassData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={COLORS.variantClass[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {consequenceData.length > 0 && (
          <div className="variant-summary-item" style={{ width: '100%', maxWidth: '100%' }}>
            <h3 className="variant-summary-title">Variant Consequence</h3>
            <div style={{ width: '100%', height: '450px', position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={consequenceData} layout="vertical" margin={{ left: 0, right: 0, top: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                  <XAxis
                    type="number"
                    label={{ value: 'Count', position: 'insideBottom', offset: -10 }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={170}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.85)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px',
                      color: 'white'
                    }}
                    itemStyle={{ color: 'white' }}
                    labelStyle={{ color: 'white' }}
                    formatter={(value, _name, props) => [`Count: ${value}`, `Impact: ${props.payload.impact}`]}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {consequenceData.map((entry, i) => (
                      <Cell key={`cell-${i}`} fill={COLORS.impact[entry.impact] || '#94a3b8'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const NetworkRenderer = ({ section, activeSub }) => (
  <MultiVariantNetwork data={(section.tables.find(t => t.name === activeSub)?.data || '')} />
);

// Helper component for chart controls (search + top X + export)
const ChartControls = ({ search, setSearch, top, setTop, onExportSVG, onExportPNG, totalCount }) => (
  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <input
      type="text"
      placeholder="Search..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      style={{
        flex: 1,
        minWidth: '120px',
        padding: '4px 8px',
        fontSize: '11px',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        backgroundColor: 'var(--background-secondary)',
        color: 'var(--text-primary)'
      }}
    />
    <select
      value={top}
      onChange={(e) => setTop(parseInt(e.target.value))}
      style={{
        padding: '4px 8px',
        fontSize: '11px',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        backgroundColor: 'var(--background-secondary)',
        color: 'var(--text-primary)'
      }}
      className="chart-control-select"
    >
      <option value={5}>Top 5</option>
      <option value={10}>Top 10</option>
      <option value={15}>Top 15</option>
      <option value={20}>Top 20</option>
      <option value={30}>Top 30</option>
      <option value={50}>Top 50</option>
      <option value={totalCount || 999}>All ({totalCount || 0})</option>
    </select>
    <button
      onClick={onExportSVG}
      style={{
        padding: '4px 8px',
        fontSize: '10px',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        backgroundColor: 'var(--background-secondary)',
        color: 'var(--text-primary)',
        cursor: 'pointer'
      }}
      title="Export as SVG"
    >
      SVG
    </button>
    <button
      onClick={onExportPNG}
      style={{
        padding: '4px 8px',
        fontSize: '10px',
        border: '1px solid var(--border-color)',
        borderRadius: '4px',
        backgroundColor: 'var(--background-secondary)',
        color: 'var(--text-primary)',
        cursor: 'pointer'
      }}
      title="Export as PNG"
    >
      PNG
    </button>
  </div>
);

const EnrichmentVisualizationPanel = ({ goCountData, goEnrichData, goAllData, keggEnrichData }) => {
const text = React.useCallback(
  (v) => (typeof v === 'string' ? v : (v && v.text) ? v.text : ''),
  []
);
const goCountStr    = React.useMemo(() => text(goCountData),    [text, goCountData]);
const goEnrichStr   = React.useMemo(() => text(goEnrichData),   [text, goEnrichData]);
const goAllStr      = React.useMemo(() => text(goAllData),      [text, goAllData]);
const keggEnrichStr = React.useMemo(() => text(keggEnrichData), [text, keggEnrichData]);
  const [goCountSearch, setGoCountSearch] = useState('');
  const [goCountTop, setGoCountTop] = useState(15);
  const [goEnrichSearch, setGoEnrichSearch] = useState('');
  const [goEnrichTop, setGoEnrichTop] = useState(15);
  const [goGeneTop, setGoGeneTop] = useState(3);
  const [keggSearch, setKeggSearch] = useState('');
  const [keggTop, setKeggTop] = useState(15);

  // Refs for charts
  const goCountChartRef = React.useRef(null);
  const goEnrichChartRef = React.useRef(null);
  const goGeneChartRef = React.useRef(null);
  const keggChartRef = React.useRef(null);

  // Export helper functions
  const exportChartAsSVG = (chartRef, filename) => {
    if (!chartRef.current) return;
    const svgElement = chartRef.current.container.querySelector('svg');
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportChartAsPNG = (chartRef, filename) => {
    if (!chartRef.current) return;
    const svgElement = chartRef.current.container.querySelector('svg');
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `${filename}.png`;
        link.click();
        URL.revokeObjectURL(pngUrl);
        URL.revokeObjectURL(url);
      });
    };
    img.src = url;
  };

  // Debug logging
  React.useEffect(() => {
    console.log('[EnrichmentVisualizationPanel] Data received:', {
      goCountLength: goCountData?.length || 0,
      goEnrichLength: goEnrichData?.length || 0,
      goAllLength: goAllData?.length || 0,
      keggEnrichLength: keggEnrichData?.length || 0
    });
  }, [goCountData, goEnrichData, goAllData, keggEnrichData]);

  // Parse GO count data (TERM ONTOLOGY count format)
  const goCountChartDataRaw = React.useMemo(() => {
    if (!goCountData) return [];

    // Safety check: ensure goCountData is a string
    if (typeof goCountData !== 'string') {
      console.error('[EnrichmentVisualizationPanel] goCountData is not a string:', typeof goCountData, goCountData);
      return [];
    }

const lines = goCountStr.trim().split('\n');
    if (lines.length <= 1) return [];

    return lines.slice(1).map(line => {
      const [term, ontology, count] = line.split('\t');
      return {
        term: term || '',
        ontology: ontology || '',
        count: parseInt(count) || 0
      };
    });
  }, [goCountData]);

  // Filter and limit GO count data
  const goCountChartData = React.useMemo(() => {
    let filtered = goCountChartDataRaw;
    if (goCountSearch.trim()) {
      const query = goCountSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.term.toLowerCase().includes(query) ||
        item.ontology.toLowerCase().includes(query)
      );
    }
    return filtered.slice(0, goCountTop);
  }, [goCountChartDataRaw, goCountSearch, goCountTop]);

  // Parse GO enrichment data (ID Description GeneRatio BgRatio pvalue p.adjust qvalue geneID Count Type)
  const goEnrichChartDataRaw = React.useMemo(() => {
    if (!goEnrichData) return [];

    // Safety check: ensure goEnrichData is a string
    if (typeof goEnrichData !== 'string') {
      console.error('[EnrichmentVisualizationPanel] goEnrichData is not a string:', typeof goEnrichData, goEnrichData);
      return [];
    }

const lines = goEnrichStr.trim().split('\n');
    if (lines.length <= 1) return [];

    const headers = lines[0].split('\t');
    const descIdx = headers.findIndex(h => h === 'Description');
    const pvalIdx = headers.findIndex(h => h === 'pvalue');
    const countIdx = headers.findIndex(h => h === 'Count');
    const typeIdx = headers.findIndex(h => h === 'Type');

    return lines.slice(1).map(line => {
      const cols = line.split('\t');
      return {
        description: cols[descIdx] || '',
        pvalue: parseFloat(cols[pvalIdx]) || 0,
        negLogPvalue: -Math.log10(parseFloat(cols[pvalIdx]) || 1),
        count: parseInt(cols[countIdx]) || 0,
        type: cols[typeIdx] || ''
      };
    });
  }, [goEnrichData]);

  // Filter and limit GO enrichment data
const goEnrichChartData = React.useMemo(() => {
  let filtered = goEnrichChartDataRaw;
  if (goEnrichSearch.trim()) {
    const q = goEnrichSearch.toLowerCase();
    filtered = filtered.filter(d =>
      d.description.toLowerCase().includes(q) ||
      d.type.toLowerCase().includes(q)
    );
  }
  // sort by significance, then put the largest at the TOP (last item -> top row)
  const sorted = [...filtered].sort((a, b) =>
    (b.negLogPvalue - a.negLogPvalue) || (b.count - a.count)
  );
  return sorted.slice(0, goEnrichTop).reverse();
}, [goEnrichChartDataRaw, goEnrichSearch, goEnrichTop]);



  // Parse KEGG enrichment data (ID Description GeneRatio BgRatio pvalue p.adjust qvalue geneID Count)
  const keggEnrichChartDataRaw = React.useMemo(() => {
    if (!keggEnrichData) return [];

    // Safety check: ensure keggEnrichData is a string
    if (typeof keggEnrichData !== 'string') {
      console.error('[EnrichmentVisualizationPanel] keggEnrichData is not a string:', typeof keggEnrichData, keggEnrichData);
      return [];
    }

const lines = keggEnrichStr.trim().split('\n');
    if (lines.length <= 1) return [];

    const headers = lines[0].split('\t');
    const descIdx = headers.findIndex(h => h === 'Description');
    const pvalIdx = headers.findIndex(h => h === 'pvalue');
    const countIdx = headers.findIndex(h => h === 'Count');

    return lines.slice(1).map(line => {
      const cols = line.split('\t');
      return {
        description: cols[descIdx] || '',
        pvalue: parseFloat(cols[pvalIdx]) || 0,
        negLogPvalue: -Math.log10(parseFloat(cols[pvalIdx]) || 1),
        count: parseInt(cols[countIdx]) || 0
      };
    });
  }, [keggEnrichData]);

const keggEnrichChartData = React.useMemo(() => {
  let filtered = keggEnrichChartDataRaw;
  if (keggSearch.trim()) {
    const q = keggSearch.toLowerCase();
    filtered = filtered.filter(d => d.description.toLowerCase().includes(q));
  }
  const sorted = [...filtered].sort((a, b) =>
    (b.negLogPvalue - a.negLogPvalue) || (b.count - a.count)
  );
  return sorted.slice(0, keggTop).reverse();
}, [keggEnrichChartDataRaw, keggSearch, keggTop]);


  // Parse GO-all data (SYMBOL GO TERM ONTOLOGY) - Gene distribution by ontology
  const goGeneDistribution = React.useMemo(() => {
    if (!goAllData) return [];

    // Safety check: ensure goAllData is a string
    if (typeof goAllData !== 'string') {
      console.error('[EnrichmentVisualizationPanel] goAllData is not a string:', typeof goAllData, goAllData);
      return [];
    }

const lines = goAllStr.trim().split('\n');
    if (lines.length <= 1) return [];

    const ontologyCounts = { BP: new Set(), MF: new Set(), CC: new Set() };

    lines.slice(1).forEach(line => {
      const [symbol, go, term, ontology] = line.split('\t');
      if (symbol && ontology && ontologyCounts[ontology]) {
        ontologyCounts[ontology].add(symbol);
      }
    });

    return [
      { ontology: 'Biological Process', code: 'BP', count: ontologyCounts.BP.size },
      { ontology: 'Molecular Function', code: 'MF', count: ontologyCounts.MF.size },
      { ontology: 'Cellular Component', code: 'CC', count: ontologyCounts.CC.size }
    ].filter(item => item.count > 0);
  }, [goAllData]);

  // Use CSS variables to respect theme
  const GO_COLORS = {
    'BP': 'var(--accent-primary)',    // Special blue for BP
    'MF': '#10b981',                   // Keep green for MF
    'CC': '#f59e0b'                    // Keep orange for CC
  };

  // Legend for GO colors
  const GOLegend = () => (
    <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem', backgroundColor: 'var(--background-secondary)', borderRadius: '6px', marginBottom: '0.5rem', fontSize: '11px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--accent-primary)', borderRadius: '2px' }}></div>
        <span style={{ color: 'var(--text-primary)' }}>BP (Biological Process)</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <div style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
        <span style={{ color: 'var(--text-primary)' }}>MF (Molecular Function)</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <div style={{ width: '12px', height: '12px', backgroundColor: '#f59e0b', borderRadius: '2px' }}></div>
        <span style={{ color: 'var(--text-primary)' }}>CC (Cellular Component)</span>
      </div>
    </div>
  );

  // Debug logging for chart data
  React.useEffect(() => {
    console.log('[EnrichmentVisualizationPanel] Chart data lengths:', {
      goCountChart: goCountChartData.length,
      goEnrichChart: goEnrichChartData.length,
      goGeneDistribution: goGeneDistribution.length,
      keggEnrichChart: keggEnrichChartData.length
    });
  }, [goCountChartData, goEnrichChartData, goGeneDistribution, keggEnrichChartData]);

  return (
    <div style={{ padding: '1rem', borderLeft: '1px solid var(--border-color)', minWidth: '400px', maxWidth: '600px', overflowY: 'auto', height: '100%' }}>
      <h3 style={{ margin: '0 0 1rem 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
        Enrichment Analysis Visualizations
      </h3>

      {goCountChartDataRaw.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
            GO Term Counts
          </h4>
          <GOLegend />
          <ChartControls
            search={goCountSearch}
            setSearch={setGoCountSearch}
            top={goCountTop}
            setTop={setGoCountTop}
            onExportSVG={() => exportChartAsSVG(goCountChartRef, 'go-term-counts')}
            onExportPNG={() => exportChartAsPNG(goCountChartRef, 'go-term-counts')}
            totalCount={goCountChartDataRaw.length}
          />
          {goCountChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart ref={goCountChartRef} data={goCountChartData} layout="vertical" margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-primary)' }} />
                <YAxis
                  type="category"
                  dataKey="term"
                  width={150}
                  tick={{ fontSize: 9, fill: 'var(--text-primary)' }}
                  interval={0}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#ffffff',
                    fontSize: '11px'
                  }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {goCountChartData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={GO_COLORS[entry.ontology] || '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No results match your search
            </div>
          )}
        </div>
      )}

      {goEnrichChartDataRaw.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
            GO Enrichment Significance
          </h4>
          <GOLegend />
          <ChartControls
            search={goEnrichSearch}
            setSearch={setGoEnrichSearch}
            top={goEnrichTop}
            setTop={setGoEnrichTop}
            onExportSVG={() => exportChartAsSVG(goEnrichChartRef, 'go-enrichment')}
            onExportPNG={() => exportChartAsPNG(goEnrichChartRef, 'go-enrichment')}
            totalCount={goEnrichChartDataRaw.length}
          />
          {goEnrichChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart ref={goEnrichChartRef} margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis
                  type="number"
                  dataKey="negLogPvalue"
                  label={{ value: '-log10(p-value)', position: 'insideBottom', offset: -5, fontSize: 10, fill: 'var(--text-primary)' }}
                  tick={{ fontSize: 10, fill: 'var(--text-primary)' }}
                />
                <YAxis
                  type="category"
                  dataKey="description"
                  width={150}
                  tick={{ fontSize: 9, fill: 'var(--text-primary)' }}
                  interval={0}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#ffffff',
                    fontSize: '11px'
                  }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                  formatter={(_, __, props) => [
                    `p-value: ${props.payload.pvalue.toExponential(2)}`,
                    `Count: ${props.payload.count}`
                  ]}
                />
                <Scatter data={goEnrichChartData}>
                  {goEnrichChartData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={GO_COLORS[entry.type] || 'var(--accent-primary)'} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No results match your search
            </div>
          )}
        </div>
      )}

      {keggEnrichChartDataRaw.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
            KEGG Pathway Enrichment
          </h4>
          <ChartControls
            search={keggSearch}
            setSearch={setKeggSearch}
            top={keggTop}
            setTop={setKeggTop}
            onExportSVG={() => exportChartAsSVG(keggChartRef, 'kegg-enrichment')}
            onExportPNG={() => exportChartAsPNG(keggChartRef, 'kegg-enrichment')}
            totalCount={keggEnrichChartDataRaw.length}
          />
          {keggEnrichChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <ScatterChart ref={keggChartRef} margin={{ left: 0, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis
                  type="number"
                  dataKey="negLogPvalue"
                  label={{ value: '-log10(p-value)', position: 'insideBottom', offset: -5, fontSize: 10, fill: 'var(--text-primary)' }}
                  tick={{ fontSize: 10, fill: 'var(--text-primary)' }}
                />
                <YAxis
                  type="category"
                  dataKey="description"
                  width={150}
                  tick={{ fontSize: 9, fill: 'var(--text-primary)' }}
                  interval={0}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#ffffff',
                    fontSize: '11px'
                  }}
                  labelStyle={{ color: '#ffffff' }}
                  itemStyle={{ color: '#ffffff' }}
                  formatter={(_, __, props) => [
                    `p-value: ${props.payload.pvalue.toExponential(2)}`,
                    `Count: ${props.payload.count}`
                  ]}
                />
                <Scatter data={keggEnrichChartData} fill="var(--accent-primary)" />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No results match your search
            </div>
          )}
        </div>
      )}

      {goGeneDistribution.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
            Gene Distribution by Ontology
          </h4>
          <GOLegend />
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => exportChartAsSVG(goGeneChartRef, 'gene-distribution')}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                backgroundColor: 'var(--background-secondary)',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
              title="Export as SVG"
            >
              SVG
            </button>
            <button
              onClick={() => exportChartAsPNG(goGeneChartRef, 'gene-distribution')}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                backgroundColor: 'var(--background-secondary)',
                color: 'var(--text-primary)',
                cursor: 'pointer'
              }}
              title="Export as PNG"
            >
              PNG
            </button>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart ref={goGeneChartRef} data={goGeneDistribution} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="ontology" tick={{ fontSize: 10, fill: 'var(--text-primary)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-primary)' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#ffffff',
                  fontSize: '11px'
                }}
                labelStyle={{ color: '#ffffff' }}
                itemStyle={{ color: '#ffffff' }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {goGeneDistribution.map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={GO_COLORS[entry.code] || '#94a3b8'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {goCountChartData.length === 0 && goEnrichChartData.length === 0 && goGeneDistribution.length === 0 && keggEnrichChartData.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '12px' }}>
          No visualization data available
        </div>
      )}
    </div>
  );
};

const EnrichmentRenderer = ({ section, activeSub, setActiveSub, jobData, onNewRunFiles }) => {
  const [showGeneModal, setShowGeneModal] = useState(false);
  const [selectedGenes, setSelectedGenes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [geneSearch, setGeneSearch] = useState('');
  const [dynamicRunTables, setDynamicRunTables] = useState([]); // new: appended after polling
  const [dynamicRunVisData, setDynamicRunVisData] = useState({}); // Store visualization data from dynamically fetched runs
  const [isPolling, setIsPolling] = useState(false); // Track if polling is active
  const pollingRef = React.useRef(null);
  const knownRunsRef = React.useRef(new Set(
    (section?.tables || [])
      .map(t => t?.name)
      .filter(Boolean)
      .map(n => (n.match(/GOKEGG_Run_(\d+)/)?.[1]))
      .filter(Boolean)
      .map(Number)
  ));

  const filteredGenes = React.useMemo(() => {
    const genes = section?.allGenes || [];
    const q = geneSearch.trim().toLowerCase();
    if (!q) return genes;
    return genes.filter(g => g && g.toLowerCase().includes(q));
  }, [geneSearch, section?.allGenes]);

  // API base: reuse same origin as keggAnalysis
  const apiOrigin = React.useMemo(() => {
    const u = new URL(getApiEndpoint('keggAnalysis'), window.location.href);
    return u.origin; // e.g., https://www.szaportal.com
  }, []);
  const idxUrl = React.useMemo(() => `${apiOrigin}/jobs/enrichment-index/${jobData?.job_id}`, [apiOrigin, jobData?.job_id]);
  const runUrl = React.useCallback((n) => `${apiOrigin}/jobs/enrichment-run/${jobData?.job_id}/${n}`, [apiOrigin, jobData?.job_id]);

  const splitGoEnrichLocal = splitGoEnrich; // reuse

  // Poll tiny index; when a run flips to "completed", fetch just its GO/KEGG text and append tables
  const startLightPolling = React.useCallback(() => {
    if (!jobData?.job_id) {
      console.log('[EnrichmentRenderer] Cannot start polling: no job_id');
      return;
    }

    console.log('[EnrichmentRenderer] Starting polling for job:', jobData.job_id);

    // clear any old poller
    if (pollingRef.current) {
      console.log('[EnrichmentRenderer] Clearing existing polling interval');
      clearInterval(pollingRef.current);
    }

    setIsPolling(true);
    console.log('[EnrichmentRenderer] State: isPolling set to true');

    const tick = async () => {
      try {
        console.log('[EnrichmentRenderer] Polling tick - fetching index:', idxUrl);
        const r = await fetch(idxUrl, { cache: 'no-store' });
        console.log('[EnrichmentRenderer] Index response:', { ok: r.ok, status: r.status });

        if (!r.ok) return;
        const j = await r.json();

        console.log('[EnrichmentRenderer] Index data:', {
          hasRuns: Array.isArray(j?.runs),
          runCount: j?.runs?.length || 0,
          runs: j?.runs
        });

        if (!Array.isArray(j?.runs)) return;

        // completed runs, newest first
        const completed = j.runs.filter(x => x.status === 'completed').sort((a, b) => a.run - b.run);
        console.log('[EnrichmentRenderer] Completed runs:', {
          count: completed.length,
          runs: completed.map(r => ({ run: r.run, status: r.status }))
        });

        for (const run of completed) {
          if (knownRunsRef.current.has(run.run)) {
            console.log(`[EnrichmentRenderer] Run ${run.run} already known, skipping`);
            continue;
          }

          console.log(`[EnrichmentRenderer] New completed run detected: ${run.run}, fetching data...`);
          const rr = await fetch(runUrl(run.run), { cache: 'no-store' });
          console.log(`[EnrichmentRenderer] Run ${run.run} response:`, { ok: rr.ok, status: rr.status });

          if (!rr.ok) continue;
          const payload = await rr.json();

          console.log(`[EnrichmentRenderer] Run ${run.run} payload:`, {
            hasGO: !!payload?.go?.text,
            hasKEGG: !!payload?.kegg?.text,
            hasGoCount: !!payload?.goCount,
            hasGoEnrich: !!payload?.goEnrich,
            hasGoAll: !!payload?.goAll,
            hasKeggEnrich: !!payload?.keggEnrich
          });

const newTables = [];
if (payload?.kegg?.text) {
  newTables.push({ name: `GOKEGG_Run_${run.run}: KEGG Pathways`, data: payload.kegg.text });
}


          console.log(`[EnrichmentRenderer] Run ${run.run} created tables:`, newTables.map(t => t.name));

          // Store visualization data for this run (if available in payload)
          if (payload?.goCount || payload?.goEnrich || payload?.goAll || payload?.keggEnrich) {
            console.log(`[EnrichmentRenderer] Run ${run.run} storing visualization data`);
setDynamicRunVisData(prev => ({
  ...prev,
  [run.run]: {
    goCount:    payload.goCount?.text    ?? '',
    goEnrich:   payload.goEnrich?.text   ?? '',
    goAll:      payload.goAll?.text      ?? '',
    keggEnrich: payload.keggEnrich?.text ?? ''
  }
}));
// Push the raw files for Export All in the modal
if (onNewRunFiles) {
  const newFiles = [];
  const add = (obj, fallback) => {
    if (obj?.text) {
      const name = obj.rel || fallback;
      newFiles.push({ name, data: obj.text });
    }
  };
  // include KEGG tab + viz files
  add(payload.kegg,        `GOKEGG_Run_${run.run}/KEGG-enrich.txt`);
  add(payload.goCount,     `GOKEGG_Run_${run.run}/user_selected_GO-all-count.txt`);
  add(payload.goEnrich,    `GOKEGG_Run_${run.run}/user_selected_GO-all-enrich.txt`);
  add(payload.goAll,       `GOKEGG_Run_${run.run}/user_selected_GO-all.txt`);
  add(payload.keggEnrich,  `GOKEGG_Run_${run.run}/user_selected_KEGG-all-enrich.txt`);
  if (newFiles.length) onNewRunFiles(newFiles);
}

          }
const completedNow = payload?.status === 'completed';

// Always mark completed as known to prevent reprocessing
if (completedNow) {
  knownRunsRef.current.add(run.run);
}

// If we added tables, keep your existing block (setDynamicRunTables, setActiveSub, etc.)
if (newTables.length) {
            console.log(`[EnrichmentRenderer] Run ${run.run} adding ${newTables.length} tables to state`);
            setDynamicRunTables(prev => [...prev, ...newTables]);
            knownRunsRef.current.add(run.run);

            // optional: jump user to latest KEGG table
            const latestKegg = newTables.find(t => /KEGG Pathways$/.test(t.name));
            if (latestKegg) {
              console.log(`[EnrichmentRenderer] Run ${run.run} switching to table:`, latestKegg.name);
              setActiveSub(latestKegg.name);
            }

            // Stop polling after successfully processing a new run
            console.log(`[EnrichmentRenderer] New run ${run.run} processed, stopping polling`);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setIsPolling(false);
            console.log('[EnrichmentRenderer] State: isPolling set to false (new run completed)');
          }
          if (completedNow) {
  if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  setIsPolling(false);
  console.log(`[EnrichmentRenderer] Stopped polling: run ${run.run} is completed`);
}
        }
      } catch (err) {
        console.error('[EnrichmentRenderer] Polling error:', err);
      }
    };

    // initial probe + interval
    console.log('[EnrichmentRenderer] Starting initial poll and setting 6s interval');
    tick();
    pollingRef.current = setInterval(tick, 6000);  // ~6s
  }, [idxUrl, runUrl, jobData?.job_id, setActiveSub, splitGoEnrichLocal]);

  // stop polling when unmounting
  React.useEffect(() => () => {
    console.log('[EnrichmentRenderer] Component unmounting, stopping polling');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      setIsPolling(false);
      console.log('[EnrichmentRenderer] State: isPolling set to false (unmount)');
    }
  }, []);

  // Log state changes
  React.useEffect(() => {
    console.log('[EnrichmentRenderer] State change: isPolling =', isPolling);
  }, [isPolling]);

  React.useEffect(() => {
    console.log('[EnrichmentRenderer] State change: isLoading =', isLoading);
  }, [isLoading]);

  React.useEffect(() => {
    console.log('[EnrichmentRenderer] State change: dynamicRunTables =', {
      count: dynamicRunTables.length,
      tables: dynamicRunTables.map(t => t.name)
    });
  }, [dynamicRunTables]);

  React.useEffect(() => {
    console.log('[EnrichmentRenderer] State change: dynamicRunVisData =', {
      runs: Object.keys(dynamicRunVisData),
      data: dynamicRunVisData
    });
  }, [dynamicRunVisData]);

  const handleGenerateAnalysis = () => {
    setShowGeneModal(true);
    setSelectedGenes([]);
    setGeneSearch('');
  };

  const handleSubmit = async () => {
    const apiUrl = getApiEndpoint('keggAnalysis');
    if (!apiUrl) {
      console.log('[EnrichmentRenderer] No API URL available');
      return;
    }

    console.log('[EnrichmentRenderer] Submitting analysis:', {
      job_id: jobData?.job_id,
      geneCount: selectedGenes.length,
      genes: selectedGenes
    });

    setIsLoading(true);
    console.log('[EnrichmentRenderer] State: isLoading set to true');

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobData?.job_id, genes: selectedGenes })
      });

      console.log('[EnrichmentRenderer] Submit response:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText
      });

      if (response.ok) {
        setShowGeneModal(false);
        console.log('[EnrichmentRenderer] Analysis submitted successfully, starting polling');
        // start tiny polling; do NOT refetch whole job files
        startLightPolling();
      } else {
        const errorText = await response.text();
        console.error('[EnrichmentRenderer] Analysis failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('[EnrichmentRenderer] Error generating analysis:', error);
    } finally {
      setIsLoading(false);
      console.log('[EnrichmentRenderer] State: isLoading set to false');
    }
  };

  const toggleGene = (g) => {
    setSelectedGenes(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  // Effective table list = static organizer tables + newly appended run tables
  const { tablesWithActive, resolvedActiveSub } = React.useMemo(() => {
    const tables = [...(section?.tables || []), ...dynamicRunTables];
    if (!tables.length) {
      return { tablesWithActive: [], resolvedActiveSub: undefined };
    }

    const activeName = tables.some(t => t.name === activeSub) ? activeSub : tables[0].name;
    return {
      tablesWithActive: tables.map(table => ({
        ...table,
        active: table.name === activeName
      })),
      resolvedActiveSub: activeName
    };
  }, [section?.tables, dynamicRunTables, activeSub]);

  const currentData = React.useMemo(() => {
    if (!resolvedActiveSub) return '';
    const hit = tablesWithActive.find(t => t.name === resolvedActiveSub);
    return hit ? hit.data : '';
  }, [tablesWithActive, resolvedActiveSub]);

  // Detect if current active table is from a GOKEGG_Run (not the default "KEGG Pathways")
  const currentRunNumber = React.useMemo(() => {
    if (!resolvedActiveSub) return null;
    const match = resolvedActiveSub.match(/GOKEGG_Run_(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, [resolvedActiveSub]);

  // Check if current table is a default "All" table (GO BP, GO MF, GO CC, or KEGG Pathways WITHOUT run number)
  const isDefaultAllTable = React.useMemo(() => {
    if (!resolvedActiveSub) return false;
    const defaultTables = ['GO Biological Process', 'GO Molecular Function', 'GO Cellular Component', 'KEGG Pathways'];
    return defaultTables.includes(resolvedActiveSub);
  }, [resolvedActiveSub]);

  // Merge visualization data from section (initial load) and dynamic fetches
  const allRunVisData = React.useMemo(() => {
    return {
      ...(section?.runVisualizationData || {}),
      ...dynamicRunVisData
    };
  }, [section?.runVisualizationData, dynamicRunVisData]);

  const isGenerateDisabled = isLoading || selectedGenes.length === 0;
  const isSpecialState = selectedGenes.length > 0;

  // Get visualization data: either from current run OR from default "All" files
  const currentVisData = React.useMemo(() => {
    if (currentRunNumber) {
      return allRunVisData[currentRunNumber] || null;
    }
    if (isDefaultAllTable) {
      return section?.defaultAllVisualizationData || null;
    }
    return null;
  }, [currentRunNumber, isDefaultAllTable, allRunVisData, section?.defaultAllVisualizationData]);

  // Debug logging
  React.useEffect(() => {
    console.log('[EnrichmentRenderer] Current state:', {
      currentRunNumber,
      hasVisData: !!currentVisData,
      allRunVisDataKeys: Object.keys(allRunVisData),
      visDataPreview: currentVisData ? {
        hasGoCount: !!currentVisData.goCount,
        hasGoEnrich: !!currentVisData.goEnrich,
        hasKeggEnrich: !!currentVisData.keggEnrich
      } : null
    });
  }, [currentRunNumber, currentVisData, allRunVisData]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderBottom: '1px solid var(--border-color)', position: 'relative', flexShrink: 0, gap: '0.75rem' }}>
        {isPolling && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '13px' }}>
            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            <span>Performing analysis...</span>
          </div>
        )}
        <button onClick={handleGenerateAnalysis} disabled={isPolling} className="WSNG-export-all-button" style={{ opacity: isPolling ? 0.5 : 1, cursor: isPolling ? 'not-allowed' : 'pointer' }}>
          <i className="fas fa-add" aria-hidden="true"></i> Generate Analysis
        </button>

        {showGeneModal && (
          <div className="WSNG-gene-modal-content">
            <h3 style={{ margin: '0 0 1rem 0', fontSize:'13px', color: 'var(--text-primary)' }}>Select Genes for Analysis</h3>
            <div className="WSNG-gene-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input type="text" placeholder="Search genes..." value={geneSearch} onChange={e => setGeneSearch(e.target.value)} className="WSNG-gene-search-input" disabled={isLoading}/>
              {geneSearch && (
                <button type="button" className="WSNG-gene-search-clear" onClick={() => setGeneSearch('')} title="Clear search" aria-label="Clear search" disabled={isLoading}>
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              )}
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setSelectedGenes(section?.allGenes || [])} style={{ padding: '6px 12px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                Select All
              </button>
              <button onClick={() => setSelectedGenes([])} style={{ padding: '6px 12px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                Clear All
              </button>
              <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '30px' }}>
                {selectedGenes.length} / {(section?.allGenes || []).length} selected
              </span>
            </div>

            <div className="WSNG-gene-list-container">
              {filteredGenes.length === 0 ? (
                <div className="WSNG-gene-empty-state">No genes match that search.</div>
              ) : (
                filteredGenes.map(gene => (
                  <label key={gene} className="WSNG-gene-checkbox-item">
                    <input type="checkbox" checked={selectedGenes.includes(gene)} onChange={() => toggleGene(gene)} disabled={isLoading}/>
                    <span>{gene}</span>
                  </label>
                ))
              )}
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowGeneModal(false)} disabled={isLoading} style={{ padding: '8px 16px', backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={isGenerateDisabled} style={{ padding: '8px 16px', backgroundColor: isSpecialState ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: isSpecialState ? '#ffffff' : 'var(--text-primary)', border: isSpecialState ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)', borderRadius: '6px', cursor: isGenerateDisabled ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', opacity: isGenerateDisabled ? 0.45 : 1 }}>
                {isLoading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="WSNG-content-area" style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DataTable
            data={currentData}
            title={resolvedActiveSub || activeSub}
            availableTables={tablesWithActive}
            onTableSwitch={(t) => setActiveSub(t.name)}
          />
        </div>

        {currentVisData && (
          <EnrichmentVisualizationPanel
            goCountData={currentVisData.goCount}
            goEnrichData={currentVisData.goEnrich}
            goAllData={currentVisData.goAll}
            keggEnrichData={currentVisData.keggEnrich}
          />
        )}
      </div>
    </div>
  );
};


// ---------- organizer (multi-variant) ----------
export const organizeMultiVariant = (files, jobData) => {
  const findData = (rx) => findRx(files, rx)?.data || '';

  // --- 1. Variant Overview Tab (Primary Results) ---
  const mainVariantFile = findData(/(?:^|\/)[^/]+\.priorisation\.txt$/i) || findData(/(?:^|\/)[^/]+\.nodup\.txt$/i);

  const overviewTables = [
    { name: 'All (Prioritized Variants)', data: mainVariantFile },
    { name: 'Variant Consequences', data: findData(/Figures_data\/.*VariantConsequence\.txt$/i) },
    { name: 'Variant Types', data: findData(/Figures_data\/.*VariantClass\.txt$/i) },
    { name: 'ClinVar Pathogenicity', data: findData(/Figures_data\/.*ClinVarPathogenicity\.txt$/i) },
    { name: 'Affected Genes', data: findData(/Figures_data\/.*GeneAccumultate-all\.txt$/i) },
  ]
  .filter(t => t.data)
  .map((t, i) => ({ ...t, active: i === 0 }));

  const variantOverview = { tables: overviewTables };

  // Extract unique genes from SYMBOL column for enrichment analysis
  const allGenes = [];
  if (mainVariantFile) {
    const parsed = parseTSV(mainVariantFile);
    const symbolSet = new Set();
    parsed.forEach(row => {
      const symbol = row.SYMBOL;
      if (symbol && symbol.trim()) {
        symbolSet.add(symbol.trim());
      }
    });
    allGenes.push(...Array.from(symbolSet).sort());
  }

  // --- 2. Variant Summary Tab ---
  const summaryImages = findAllRx(files, /(ideogram\.png|Marker_Density\.user_selected_SNP_density\.jpg)$/i);

  const clinvarPathogenicity = findData(/Figures_data\/.*ClinVarPathogenicity\.txt$/i);
  const diseaseAccumulate = findData(/Figures_data\/.*DiseaseAccumultate-all\.txt$/i);
  const geneAccumulate = findData(/Figures_data\/.*GeneAccumultate-all\.txt$/i);
  const variantClass = findData(/Figures_data\/.*VariantClass\.txt$/i);
  const variantConsequence = findData(/Figures_data\/.*VariantConsequence\.txt$/i);

  const variantSummary = (summaryImages.length || clinvarPathogenicity || diseaseAccumulate || geneAccumulate || variantClass || variantConsequence) ? {
    images: summaryImages,
    clinvarPathogenicity,
    diseaseAccumulate,
    geneAccumulate,
    variantClass,
    variantConsequence
  } : undefined;

// --- 3. Enrichment Tab ---
// "All" (existing)
const goEnrichAll   = findData(/(?:^|\/)[^/]*GO[-_]all[-_]enrich\.txt$/i);
const keggEnrichAll = findData(/(?:^|\/)[^/]*KEGG[-_]all[-_]enrich\.txt$/i);
const goBucketsAll  = splitGoEnrich(goEnrichAll);

// Extract default "All" visualization files (for default KEGG Pathways tab)
const goCountAll    = findData(/(?:^|\/)[^/]*GO[-_]all[-_]count\.txt$/i);
const goAllMapping  = findData(/(?:^|\/)[^/]*GO[-_]all\.txt$/i);
const defaultAllVisualizationData = (goCountAll || goAllMapping || keggEnrichAll) ? {
  goCount: goCountAll,
  goEnrich: goEnrichAll,
  goAll: goAllMapping,
  keggEnrich: keggEnrichAll
} : null;

// NEW: collect any GOKEGG_Run_* sub-results
// We need both filename and content, so scan files array directly:
const gokeggRuns = (files || [])
  .filter(f => /(^|\/)GOKEGG_Run_\d+(\/|$)/i.test(f.name))
  .reduce((acc, f) => {
    const m = f.name.match(/GOKEGG_Run_(\d+)/i);
    if (!m) return acc;
    const runN = parseInt(m[1], 10);
    if (!acc[runN]) acc[runN] = [];
    acc[runN].push(f);
    return acc;
  }, {});

// Collect visualization data per run
const runVisualizationData = {};

// Turn each run into up to 4 tables, mirroring "All" labels
const runTables = Object.keys(gokeggRuns)
  .map(n => parseInt(n, 10))
  .sort((a, b) => b - a) // newest first
  .flatMap(runN => {
    const runFiles = gokeggRuns[runN];

    const pickTxt = (rx) => {
      const hit = runFiles.find(f => rx.test(f.name));
      return hit?.data || '';
    };

    const goTxt    = pickTxt(/GO-.*Enrich\.txt$/i);   // tolerate names like GO-Enrich.txt
    const keggTxt  = pickTxt(/KEGG-.*Enrich\.txt$/i); // tolerate names like KEGG-Enrich.txt
    const buckets  = splitGoEnrich(goTxt);

    // Extract visualization files for this run
    const goCountTxt = pickTxt(/user_selected_GO-all-count\.txt$/i);
    const goEnrichFullTxt = pickTxt(/user_selected_GO-all-enrich\.txt$/i);
    const goAllTxt = pickTxt(/user_selected_GO-all\.txt$/i);
    const keggEnrichFullTxt = pickTxt(/user_selected_KEGG-all-enrich\.txt$/i);

    // Store visualization data for this run
    if (goCountTxt || goEnrichFullTxt || goAllTxt || keggEnrichFullTxt) {
      runVisualizationData[runN] = {
        goCount: goCountTxt,
        goEnrich: goEnrichFullTxt,
        goAll: goAllTxt,
        keggEnrich: keggEnrichFullTxt
      };
    }

const tables = [
  keggTxt && { name: `GOKEGG_Run_${runN}: KEGG Pathways`, data: keggTxt }
].filter(Boolean);


    return tables;
  });

// Build final table list: "All" first, then runs
const enrichTables = [
  ...(keggEnrichAll ? [{ name: 'KEGG Pathways', data: keggEnrichAll }] : []),
  ...runTables
].map((t, i) => ({ ...t, active: i === 0 }));

// Extract unique genes from main variant file (unchanged)
const enrichment = enrichTables.length ? {
  tables: enrichTables,
  allGenes,
  runVisualizationData, // Visualization data for runs
  defaultAllVisualizationData // Visualization data for default "All" tabs
} : undefined;


  // --- 4. GWAS, QTLs & PGx Tab ---
  const gwasData = findData(/\.nodup\.gwas_filtered\.txt$/i) || findData(/\.nodup\.gwas\.txt$/i);
  const pgxData = findData(/\.nodup\.pgx_filtered\.txt$/i) || findData(/\.nodup\.pgx\.txt$/i);

  const qtlTables = [
    { name: 'GWAS', data: gwasData },
    { name: 'eQTL Catalog', data: findData(/\.nodup\.eqtl_catalog_filtered\.txt$/i) || findData(/\.nodup\.eqtl_catalog\.txt$/i) },
    { name: 'eQTL GTEx', data: findData(/\.nodup\.eqtl_gtex_filtered\.txt$/i) || findData(/\.nodup\.eqtl_gtex\.txt$/i) },
    { name: 'pQTL Open Target', data: findData(/\.nodup\.pqtl_otg_filtered\.txt$/i) || findData(/\.nodup\.pqtl_otg\.txt$/i) },
    { name: 'pQTL EpigraphDB', data: findData(/\.pqtl_epigraphdb_filtered\.txt$/i) },
    { name: 'Pharmacogenomics (PGx)', data: pgxData },
  ]
  .filter(t => t.data)
  .map((t, i) => ({ ...t, active: i === 0 }));

  const qtls = qtlTables.length ? { tables: qtlTables } : undefined;

  // --- 5. Network Tab ---
  const netTxt = findData(/\.network\.txt$/i);
  const network = netTxt ? { tables: [{ name: 'Gene-Variant Network', data: netTxt, active: true }] } : undefined;

  // --- 6. Exomiser Tab ---
  const phenixGenesData = findData(/Exomiser_PHENIX_PRIORITY\.genes\.tsv$/i);
  const phenixVariantsData = findData(/Exomiser_PHENIX_PRIORITY\.variants\.tsv$/i);

  console.log('Exomiser Debug:');
  console.log('All files:', files.map(f => f.name));
  console.log('PHENIX Genes data found:', !!phenixGenesData, phenixGenesData ? `(${phenixGenesData.length} chars)` : '');
  console.log('PHENIX Variants data found:', !!phenixVariantsData, phenixVariantsData ? `(${phenixVariantsData.length} chars)` : '');

  const exomiserTables = [
    { name: 'PHENIX Genes', data: phenixGenesData },
    { name: 'PHENIX Variants', data: phenixVariantsData },
  ]
  .filter(t => t.data)
  .map((t, i) => ({ ...t, active: i === 0 }));

  console.log('Exomiser tables after filter:', exomiserTables.map(t => t.name));

  const exomiserHtml = findRx(files, /Exomiser_PHENIX_PRIORITY\.html$/i);
  const exomiserJson = findRx(files, /Exomiser_PHENIX_PRIORITY\.json$/i);
  const exomiser = (exomiserTables.length || exomiserHtml) ? {
    tables: exomiserTables,
    htmlFile: exomiserHtml,
    jsonFile: exomiserJson,
  } : undefined;

  // --- Assemble All Tabs ---
  const organized = {
    'Variant Overview': variantOverview,
    ...(variantSummary ? { 'Variant Summary': variantSummary } : {}),
    ...(enrichment ? { 'Enrichment': enrichment } : {}),
    ...(qtls ? { 'GWAS, QTLs & PGx': qtls } : {}),
    ...(network ? { 'Network': network } : {}),
    ...(exomiser ? { 'Exomiser': exomiser } : {}),
  };

  const renderers = {
    'Variant Summary': (props) => <VariantSummaryRenderer {...props} />,
    'Enrichment': (props) => <EnrichmentRenderer {...props} />,
    'Network': (props) => <NetworkRenderer {...props} />,
  };

  const initial = { tab: 'Variant Overview', sub: (overviewTables[0] || {}).name };
  return { organized, renderers, initial };
};
